import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import LanguageDetector from "i18next-browser-languagedetector";
import resourcesToBackend from "i18next-resources-to-backend";

// Eager namespaces (common + nav + home).
//
// Why these three?
//   - common: cross-page strings (buttons, NotFound) referenced from many namespaces
//   - nav:    Navbar/Footer/SEO Helmet — visible on every route, can't flash
//   - home:   InstallBanner uses it and renders OUTSIDE the App-level <Suspense>,
//             so it must be synchronously available to avoid a "render without
//             provider" suspend. Also home page above-the-fold copy.
//
// All other namespaces (offers, faq, contact, privacy, terms) are loaded on
// demand by useTranslation() via i18next-resources-to-backend, which webpack
// turns into per-(lang, ns) dynamic chunks.

import enCommon from "./locales/en/common.json";
import enNav from "./locales/en/nav.json";
import enHome from "./locales/en/home.json";

import frCommon from "./locales/fr/common.json";
import frNav from "./locales/fr/nav.json";
import frHome from "./locales/fr/home.json";

import zhCommon from "./locales/zh/common.json";
import zhNav from "./locales/zh/nav.json";
import zhHome from "./locales/zh/home.json";

import zhTWCommon from "./locales/zh-TW/common.json";
import zhTWNav from "./locales/zh-TW/nav.json";
import zhTWHome from "./locales/zh-TW/home.json";

import paCommon from "./locales/pa/common.json";
import paNav from "./locales/pa/nav.json";
import paHome from "./locales/pa/home.json";

import esCommon from "./locales/es/common.json";
import esNav from "./locales/es/nav.json";
import esHome from "./locales/es/home.json";

import tlCommon from "./locales/tl/common.json";
import tlNav from "./locales/tl/nav.json";
import tlHome from "./locales/tl/home.json";

import arCommon from "./locales/ar/common.json";
import arNav from "./locales/ar/nav.json";
import arHome from "./locales/ar/home.json";

import koCommon from "./locales/ko/common.json";
import koNav from "./locales/ko/nav.json";
import koHome from "./locales/ko/home.json";

import jaCommon from "./locales/ja/common.json";
import jaNav from "./locales/ja/nav.json";
import jaHome from "./locales/ja/home.json";

const SUPPORTED_LANGS = ["en", "fr", "zh", "zh-TW", "pa", "es", "tl", "ar", "ko", "ja"];
const NAMESPACES = ["common", "nav", "home", "offers", "faq", "contact", "privacy", "terms"];
const EAGER_NAMESPACES = ["common", "nav", "home"];

const eagerResources = {
  en: { common: enCommon, nav: enNav, home: enHome },
  fr: { common: frCommon, nav: frNav, home: frHome },
  zh: { common: zhCommon, nav: zhNav, home: zhHome },
  "zh-TW": { common: zhTWCommon, nav: zhTWNav, home: zhTWHome },
  pa: { common: paCommon, nav: paNav, home: paHome },
  es: { common: esCommon, nav: esNav, home: esHome },
  tl: { common: tlCommon, nav: tlNav, home: tlHome },
  ar: { common: arCommon, nav: arNav, home: arHome },
  ko: { common: koCommon, nav: koNav, home: koHome },
  ja: { common: jaCommon, nav: jaNav, home: jaHome },
};

// Normalize region-coded BCP-47 tags (e.g. "en-US" → "en", "fr-CA" → "fr") to
// our supported set BEFORE the dynamic backend tries to import them. We keep
// "zh-TW" as-is because that's a supported variant. Anything unmapped falls
// through to load:"currentOnly" + fallbackLng:"en".
function normalizeLang(lng) {
  if (!lng) return "en";
  if (SUPPORTED_LANGS.includes(lng)) return lng;
  const base = lng.split("-")[0];
  if (SUPPORTED_LANGS.includes(base)) return base;
  return "en";
}

const lazyBackend = resourcesToBackend((language, namespace) => {
  const lang = normalizeLang(language);
  // Eager namespaces are already in `resources`; never reach the backend.
  // Defensive guard in case i18next still asks (e.g. a key forced reload):
  if (EAGER_NAMESPACES.includes(namespace) && eagerResources[lang]?.[namespace]) {
    return Promise.resolve(eagerResources[lang][namespace]);
  }
  // webpack turns this into 50 lazy chunks (10 langs × 5 lazy ns).
  return import(
    /* webpackChunkName: "i18n-[request]" */
    `./locales/${lang}/${namespace}.json`
  );
});

i18n
  .use(LanguageDetector)
  .use(lazyBackend)
  .use(initReactI18next)
  .init({
    resources: eagerResources,
    partialBundledLanguages: true,
    fallbackLng: "en",
    supportedLngs: SUPPORTED_LANGS,
    nonExplicitSupportedLngs: true,
    load: "currentOnly",
    defaultNS: "common",
    ns: EAGER_NAMESPACES,
    interpolation: {
      escapeValue: false,
    },
    detection: {
      order: ["localStorage", "navigator"],
      caches: ["localStorage"],
      lookupLocalStorage: "pointie-lang",
    },
    react: {
      // useSuspense:true is safe because everything OUTSIDE the App-level
      // <Suspense> boundary (App, InstallBanner, SEO via children) only uses
      // eager namespaces (common, nav, home), so they never suspend. Components
      // inside <Suspense> that use lazy namespaces (offers, faq, contact,
      // privacy, terms) suspend until their chunk arrives — falling back to
      // the existing <LoadingFallback /> spinner — and then hydrate cleanly.
      // This avoids the "raw key flash" and the "t().map() on a string" crash
      // that returnObjects:true callers would otherwise hit.
      useSuspense: true,
    },
  });

// Surface lazy-load failures (CDN purge mid-load, network blip) so we know
// when a user fell back to keys. Single warn per (lng, ns) pair to avoid spam.
const _failedReported = new Set();
i18n.on("failedLoading", (lng, ns, msg) => {
  const k = `${lng}|${ns}`;
  if (_failedReported.has(k)) return;
  _failedReported.add(k);
  // eslint-disable-next-line no-console
  console.warn(`[i18n] failed to load ${lng}/${ns}: ${msg}`);
});

i18n.on("languageChanged", (lng) => {
  const base = lng.split("-")[0];
  document.documentElement.lang = lng;
  document.documentElement.dir = base === "ar" ? "rtl" : "ltr";
});

const initBase = i18n.language?.split("-")[0];
document.documentElement.dir = initBase === "ar" ? "rtl" : "ltr";
document.documentElement.lang = i18n.language || "en";

export { SUPPORTED_LANGS, NAMESPACES };
export default i18n;
